import { TemplateRef } from "@angular/core";

import { format } from "date-fns";

import { environment } from '@env/environment';
import { Guid, SM } from "@models/base/helper-types";
import { CodeSnippet } from "@models/shared/code-snippet";
import { VzCrashData } from "@models/shared/crash-data";
import { IVzMenuItem } from "@models/shared/vz-menu-item";
import { VzTag } from "@models/tags/tag";
import { Task } from "@models/tasks/task";
import { StoreService } from "@services/store.service";
import { getDeviceInfo } from "./device";
import { sortDatesBy, sortStringsBy } from "./sort";

export type TasksContextMenuData = {
    hasFav?: boolean,
    noFav?: boolean,
    cg2Active?: boolean,
    cg2Backlog?: boolean,
    cg2Archive?: boolean,
    tagsAdd?: { tag: VzTag, ch?: VzTag[] }[],
    tagsRemove?: VzTag[],
    unread?: Guid[];
    canBeDeleted?: Guid[];
};

export function getContextMenuData(tasks: Task[], opts: { userId?: Guid, activeOrgId?: Guid, pGranted: SM<SM<boolean>>, tagsMap: SM<VzTag> }): TasksContextMenuData {
    const cm: TasksContextMenuData = {};
    cm.unread = [];
    cm.canBeDeleted = [];
    if (tasks.length == 1 && tasks[0]) {
        const t = tasks[0];
        cm.hasFav = t.flags.favorite;
        cm.noFav = !t.flags.favorite;
        // TODO use Task.targets to determine possible options
        // cm.cg2Active = t._cg2.active;
        // cm.cg2Backlog = t._cg2.backlog;
        // cm.cg2Archive = t._cg2.archive;
        if (!t.flags.seen) {
            cm.unread.push(t.id!);
        }
        if (t.createdBy == opts.userId || opts.pGranted[t.projectId!]?.['task.delete']) {
            cm.canBeDeleted.push(t.id!);
        }
    }
    else {
        tasks.forEach(t => {
            if (t.flags.favorite) {
                cm.hasFav = true;
            }
            else {
                cm.noFav = true;
            }
            // if (t._cg2.active) {
            //     cm!.cg2Active = true;
            // }
            // if (t._cg2.backlog) {
            //     cm!.cg2Backlog = true;
            // }
            // if (t._cg2.archive) {
            //     cm!.cg2Archive = true;
            // }
            if (!t.flags.seen) {
                cm.unread!.push(t.id!);
            }
            if (t.createdBy == opts.userId || opts.pGranted[t.projectId!]?.['task.delete']) {
                cm.canBeDeleted!.push(t.id!);
            }
        });
    }
    const tm = opts.tagsMap;
    const pm: SM<boolean> = tasks.map(t => t.projectId).filter(id => !!id).reduce((obj, id) => ({ ...obj, [id!]: true }), {});
    const ta = Object.values(tm).filter(tag => tag.orgId == opts.activeOrgId && !tag.parentId && (!tag.projectId || pm[tag.projectId])).sort(sortStringsBy('text')).map(tag => ({
        tag,
        ch: Object.values(tm).filter(t => t.orgId == opts.activeOrgId && t.parentId == tag.id).sort(sortStringsBy('text'))
    }));
    cm.tagsAdd = ta;
    const tids = tasks.map(t => t.tags || []).reduce((arr, ta) => ([...arr, ...ta]), []);
    const tags = Object.values(tm).filter(t => tids.includes(t.id!));
    cm.tagsRemove = tags.sort(sortStringsBy('_path'));
    return cm;
}

export function getTasksContextMenu(
    tasks: Task[],
    opts: {
        userId?: Guid,
        activeOrgId?: Guid,
        pGranted: SM<SM<boolean>>,
        tagsMap: SM<VzTag>,
        isInbox?: boolean,
        isTickets?: boolean,
        isMobile?: boolean,
        tagTpl: TemplateRef<any>,
        priorityTpl: TemplateRef<any>,
    }
): { title: { task?: Task; count: number } | undefined, menu: IVzMenuItem[] } | undefined {
    if (!tasks?.length) {
        return;
    }
    const cm = getContextMenuData(tasks, opts);
    const menu: IVzMenuItem[] = [];
    if (cm.unread?.length) {
        menu.push({
            id: 'markAsRead',
            title: 'Пометить как прочитанную',
            icon: 'vzi-eye',
            data: cm.unread
        });
    }
    if (opts.isInbox) {
        menu.push({
            id: 'removeFromInbox',
            title: 'Убрать из входящих',
            icon: 'vzi-cross',
            data: tasks.map(t => t.id)
        });
    }
    if (tasks.length == 1) {
        menu.push({
            id: 'copyLinkToTask',
            title: 'Скопировать ссылку на задачу',
            icon: 'vzi-link fs-xl',
            data: tasks[0].shortId
        });
    }
    if (!opts.isTickets) {
        if (tasks.length == 1 && !tasks[0].parentId) {
            menu.push({
                id: 'createSubtask',
                title: 'Создать подзадачу',
                icon: 'vzi-flow-cascade',
                data: tasks[0].id
            });
        }
        if (cm.noFav || cm.hasFav) {
            menu.push({ id: '-' });
            if (cm.noFav) {
                menu.push({
                    id: 'addToFavs',
                    title: 'Добавить в избранное',
                    icon: 'vzi-star txt-warn',
                    data: tasks.map(t => t.id)
                });
            }
            if (cm.hasFav) {
                menu.push({
                    id: 'removeFromFavs',
                    title: 'Убрать из избранного',
                    icon: 'vzi-star-o',
                    data: tasks.map(t => t.id)
                });
            }
        }
        // if (tasks.length == 1) {
        //     menu.push({
        //         id: 'setReminder',
        //         title: 'Напомнить об этой задаче',
        //         icon: 'vzi-timer',
        //         data: tasks[0].id
        //     });
        // }
        if (cm.cg2Active) {
            menu.push({
                id: 'cg2Active',
                title: 'Перевести в "Активные"',
                icon: 'vzi-fire',
                data: tasks.map(t => t.id)
            });
        }
        if (cm.cg2Backlog) {
            menu.push({
                id: 'cg2Backlog',
                title: 'Перевести в "Отложенные"',
                icon: 'vzi-stack',
                data: tasks.map(t => t.id)
            });
        }
        if (cm.cg2Archive) {
            menu.push({
                id: 'cg2Archive',
                title: 'Перевести в "Архив"',
                icon: 'vzi-done',
                data: tasks.map(t => t.id)
            });
        }
        menu.push({ id: '-' });
        menu.push({
            id: '_priority',
            title: 'Установить приоритет',
            icon: 'vzi-bookmark fs-xxl top3 mr05',
            children: [1,2,3,0].map(pr => ({
                id: 'priority',
                tpl: opts.priorityTpl,
                data: { tasks, pr }
            }))
        });
        menu.push({
            id: 'assignee',
            title: 'Назначить ответственного',
            icon: 'vzi-person-o',
            data: tasks
        });
        if (!opts.isMobile && (cm.tagsAdd?.length || cm.tagsRemove?.length)) {
            menu.push({ id: '-' });
            if (cm.tagsAdd?.length) {
                menu.push({
                    id: '_tagsAdd',
                    title: 'Добавить тег',
                    icon: 'vzi-add txt-ok fs-3xl top3 mr05 ml-025',
                    children: cm.tagsAdd.map(pt => {
                        if (pt.ch?.length) {
                            return {
                                id: 'addTag',
                                tpl: opts.tagTpl,
                                data: { tasks, tag: pt.tag },
                                children: pt.ch.map(tag => ({ id: 'addTag', tpl: opts.tagTpl, data: { tasks, tag } }))
                            };
                        }
                        else {
                            return {
                                id: 'addTag',
                                tpl: opts.tagTpl,
                                data: { tasks, tag: pt.tag }
                            }
                        }
                    })
                });
            }
            if (cm.tagsRemove?.length) {
                menu.push({
                    id: '_tagsRemove',
                    title: 'Убрать тег',
                    icon: 'vzi-minus txt-err fs-3xl top3 mr05 ml-025',
                    children: cm.tagsRemove.map(tag => ({ id: 'removeTag', tpl: opts.tagTpl, data: { tasks, tag } }))
                });
            }
        }
        if (cm.canBeDeleted?.length) {
            menu.push({ id: '-' });
            menu.push({
                id: 'delete',
                title: 'Удалить',
                icon: 'vzi-remove',
                danger: true,
                data: cm.canBeDeleted
            });
        }
    }
    return { title: { task: tasks.length == 1 ? tasks[0] : undefined, count: tasks.length }, menu};
}

export function getSnippetsForTicket(store: StoreService, cd?: VzCrashData): CodeSnippet[] {
    const logs = store.appLog.getItems().filter(item => !!item);
    const rmqLogs = store.getState('stomp').items.filter(item => !!item).sort(sortDatesBy('dt'));
    const env: any = { ...environment };
    delete env.firebaseConfig;
    const res: CodeSnippet[] = cd ? [new CodeSnippet({ title: 'Stack trace', syntax: 'plaintext', mimeType: 'text/plain', text: cd.stack })] : [];
    res.push(
        new CodeSnippet({
            title: 'Журнал работы приложения',
            syntax: 'log',
            mimeType: 'text/plain',
            text: logs.map(l =>
                format(l.dt * 1000, 'dd.MM.y HH:mm:ss.SSS')
                + (l.severity == 'warn' ? ' W ' : l.severity == 'error' ? ' E ' : '   ')
                + `${l.tag} `
                + l.items.join(' ')
            ).join('\n') || ' '
        }),
        new CodeSnippet({
            title: 'Environment',
            syntax: 'json',
            mimeType: 'application/json',
            text: JSON.stringify(env, undefined, 4) || ' '
        }),
        new CodeSnippet({
            title: 'RMQ Logs',
            syntax: 'log',
            mimeType: 'application/json',
            text: rmqLogs.map(l =>
                format(l.dt, 'dd.MM.y HH:mm:ss.SSS')
                + (l.rc ? ' C ' : '   ')
                + `${l.o}_${l.t} `
                + `${l.e}.${l.rk} `
                + 'ids: [' + (l.ids || []).join(',') + ']'
                + (l.data ? ' data: ' + JSON.stringify(l.data) : '')
            ).join('\n') + ' '
        }),
        new CodeSnippet({
            title: 'Device',
            syntax: 'json',
            mimeType: 'application/json',
            text: JSON.stringify(getDeviceInfo(), undefined, 4) || ' '
        }),
    );
    try {
        const err = localStorage?.getItem('_vizorro_last_token_update_permanent_error');
        if (err) {
            res.push(new CodeSnippet({
                title: 'last_token_update_permanent_error',
                syntax: 'json',
                mimeType: 'application/json',
                text: err || ' '
            }));
        }
    } catch (_) { }
    return res;
}
